import { onScopeDispose } from 'vue'

type DirtyCheck = () => boolean

/**
 * Module-level registry of "this screen holds unsaved input" predicates.
 *
 * Switching customer tears the whole app down (see `LCustomerSwitcher`), so it
 * has to know whether that would lose work — but the forms holding that state
 * are scattered across views and none of them is an ancestor of the switcher.
 * Each form registers a predicate on setup; it is dropped again automatically
 * when the component's effect scope is disposed.
 */
const checks = new Set<DirtyCheck>()

/** Register a dirty-state predicate for the lifetime of the calling component. */
export function useUnsavedChanges(isDirty: DirtyCheck): void {
  checks.add(isDirty)
  onScopeDispose(() => {
    checks.delete(isDirty)
  })
}

/** True when any currently mounted form reports unsaved input. */
export function hasUnsavedChanges(): boolean {
  for (const isDirty of checks) {
    try {
      if (isDirty()) return true
    } catch {
      // A predicate reading half-initialised state must not be able to wedge
      // the switcher — treat it as "nothing to lose".
    }
  }
  return false
}
